import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import CheckCircleSolid from '../../icons/CheckCircleSolid';
import InfoSolid from '../../icons/InfoSolid';
import WarningDiamondSolid from '../../icons/WarningDiamondSolid';
import WarningTriangleSolid from '../../icons/WarningTriangleSolid';
import { cn } from '../../utils/cn';
const alertVariants = cva('relative w-full rounded-lg p-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [--alert-icon-color:currentColor] [&>svg]:size-5 has-[[data-slot=alert-title]]:[&>svg]:translate-y-px [&>svg]:text-[var(--alert-icon-color)]', {
  variants: {
    variant: {
      default: 'bg-card text-card-foreground',
      destructive: 'bg-destructive text-card-foreground [--alert-icon-color:var(--destructive-foreground)]',
      warning: 'bg-warning text-card-foreground [--alert-icon-color:var(--warning-foreground)]',
      info: 'bg-info text-card-foreground [--alert-icon-color:var(--info-foreground)]',
      success: 'bg-success text-card-foreground [--alert-icon-color:var(--success-foreground)]',
      primary: 'bg-primary text-primary-foreground',
      muted: 'bg-muted text-muted-foreground'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
type AlertVariant = NonNullable<VariantProps<typeof alertVariants>['variant']>;
const variantIcons: Partial<Record<AlertVariant, React.ComponentType<{
  className?: string;
}>>> = {
  info: InfoSolid,
  success: CheckCircleSolid,
  warning: WarningTriangleSolid,
  destructive: WarningDiamondSolid
};
function renderDefaultIcon(variant: AlertVariant): React.ReactNode {
  const IconComponent = variantIcons[variant];
  return IconComponent ? <IconComponent /> : null;
}
interface AlertProps extends React.ComponentProps<'div'>, VariantProps<typeof alertVariants> {
  /**
   * Icon rendered to the left of the alert content.
   *
   * - `undefined` (default): use the solid icon associated with the variant — `info` →
   *   info circle, `success` → check circle, `warning` → triangle warning, `destructive`
   *   → diamond warning. Variants without a default (`default`, `primary`, `muted`) render
   *   no icon.
   * - A React node: overrides the default with that node.
   * - `null` or `false`: render no icon, even if the variant has a default.
   */
  icon?: React.ReactNode | null | false;
}
function Alert(t0) {
  const $ = _c(17);
  let children;
  let className;
  let icon;
  let props;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      icon,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = props;
    $[5] = variant;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    props = $[4];
    variant = $[5];
  }
  let t1;
  if ($[6] !== icon || $[7] !== variant) {
    t1 = icon === undefined ? renderDefaultIcon(variant ?? "default") : icon;
    $[6] = icon;
    $[7] = variant;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const renderedIcon = t1;
  let t2;
  if ($[9] !== className || $[10] !== variant) {
    t2 = cn(alertVariants({
      variant
    }), className);
    $[9] = className;
    $[10] = variant;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== children || $[13] !== props || $[14] !== renderedIcon || $[15] !== t2) {
    t3 = <div data-slot="alert" role="alert" className={t2} {...props}>{renderedIcon}{children}</div>;
    $[12] = children;
    $[13] = props;
    $[14] = renderedIcon;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
function AlertTitle(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("col-start-2 line-clamp-1 min-h-4 text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="alert-title" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function AlertDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("col-start-2 grid justify-items-start gap-1 text-xs", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="alert-description" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Alert, alertVariants, AlertDescription, AlertTitle };